import React, { useContext, useEffect, useReducer, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useHelps from "../hooks/useHelps";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListSubheader from "@material-ui/core/ListSubheader";
import Badge from "@material-ui/core/Badge";
import Collapse from "@material-ui/core/Collapse";
import List from "@material-ui/core/List";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";

import {
  LuActivity,
  LuArrowLeftRight,
  LuBanknote,
  LuBellRing,
  LuBuilding2,
  LuCalendar,
  LuCake,
  LuChevronDown,
  LuChevronUp,
  LuCode,
  LuColumns,
  LuFileBarChart,
  LuGitBranch,
  LuHelpCircle,
  LuHistory,
  LuLayoutDashboard,
  LuList,
  LuMegaphone,
  LuMessageCircle,
  LuMessagesSquare,
  LuNetwork,
  LuPaperclip,
  LuPlug,
  LuSettings,
  LuShare2,
  LuSmartphone,
  LuSparkles,
  LuTag,
  LuUser,
  LuUsers,
  LuWallet,
  LuWorkflow,
  LuZap,
} from "react-icons/lu";

import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { useActiveMenu } from "../context/ActiveMenuContext";

import { Can } from "../components/Can";

import { isArray } from "lodash";
import api from "../services/api";
import toastError from "../errors/toastError";
import usePlans from "../hooks/usePlans";
import useVersion from "../hooks/useVersion";
import { i18n } from "../translate/i18n";

import useCompanySettings from "../hooks/useSettings/companySettings";

const SECTION_LABELS = {
  operation: "Operação",
  clients: "Clientes",
  automation: "Automação",
  insights: "Insights",
  administration: "Administração",
};

const SIDEBAR_ICON_SIZE = 20;
const SIDEBAR_ICON_STROKE = 1.75;

function SidebarIcon({ icon: Icon }) {
  return (
    <Icon size={SIDEBAR_ICON_SIZE} strokeWidth={SIDEBAR_ICON_STROKE} aria-hidden />
  );
}

const useStyles = makeStyles((theme) => ({
  listItem: {
    position: "relative",
    minHeight: "44px",
    width: "calc(100% - 16px)",
    margin: "2px 8px",
    padding: "0 12px",
    borderRadius: "8px",
    color: "#B8C0CC",
    fontFamily: "'Inter', 'Roboto', sans-serif",
    transition: "background-color 160ms ease, color 160ms ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.07)",
    },
    "&:hover $listItemText": {
      color: "#FFFFFF",
    },
    "&:hover $listItemIcon": {
      color: "#FFFFFF",
    },
  },

  listItemCollapsed: {
    width: 48,
    minWidth: 48,
    maxWidth: 48,
    margin: "4px auto",
    padding: 0,
    justifyContent: "center",
  },

  listItemIcon: {
    minWidth: 40,
    width: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: "#98A2B3",
    transition: "color 160ms ease",
    "& svg": {
      width: SIDEBAR_ICON_SIZE,
      height: SIDEBAR_ICON_SIZE,
      flexShrink: 0,
    },
  },

  listItemIconCollapsed: {
    minWidth: 40,
    width: 40,
    margin: 0,
  },

  listItemText: {
    fontSize: "14px",
    color: "#B8C0CC",
    transition: "color 160ms ease",
    fontWeight: 500,
    letterSpacing: "-0.01em",
    "& .MuiTypography-root": {
      fontFamily: "'Inter', 'Roboto', sans-serif",
      fontSize: "14px",
      fontWeight: 500,
      lineHeight: 1.4,
    }
  },

  // Badge melhorado mas mantendo funcionalidade
  badge: {
    "& .MuiBadge-badge": {
      backgroundColor: "#ef4444",
      color: "#fff",
      fontSize: "0.75rem",
      fontWeight: 600,
      animation: "$pulse 2s infinite",
    }
  },

  "@keyframes pulse": {
    "0%, 100%": {
      opacity: 1,
    },
    "50%": {
      opacity: 0.7,
    }
  },

  // Melhorias para submenus mantendo estrutura original
  submenuContainer: {
    backgroundColor: "rgba(255, 255, 255, 0.035)",
  },

  // Tooltip melhorado
  customTooltip: {
    backgroundColor: theme.mode === "light" ? "#1e293b" : "#374151",
    color: "#fff",
    fontSize: "0.875rem",
    fontWeight: 500,
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    "& .MuiTooltip-arrow": {
      color: theme.mode === "light" ? "#1e293b" : "#374151",
    }
  },

  // Versão com destaque sutil
  versionContainer: {
    textAlign: "center",
    padding: "10px",
    color: "#667085",
    fontSize: "12px",
    fontWeight: 500,
    borderTop: "1px solid rgba(255, 255, 255, 0.08)",
    marginTop: "auto",
  },

  // Seções de administração com destaque sutil
  adminSection: {
    "& .MuiListSubheader-root": {
      color: theme.palette.primary.main, // Usa cor do tema
      fontSize: "0.875rem",
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    }
  },

  // Efeitos suaves para expand/collapse
  expandIcon: {
    transition: "transform 0.3s ease",
    color: "#98A2B3",
    "&.expanded": {
      transform: "rotate(180deg)",
    }
  },

  // Menu container com melhorias sutis
  menuContainer: {
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.mode === "light"
        ? "rgba(0, 0, 0, 0.1)"
        : "rgba(255, 255, 255, 0.1)",
      borderRadius: "3px",
      "&:hover": {
        background: theme.mode === "light"
          ? "rgba(0, 0, 0, 0.2)"
          : "rgba(255, 255, 255, 0.2)",
      }
    },
  },

  // Estado ativo melhorado mantendo funcionalidade original
  activeItem: {
    backgroundColor: `${theme.palette.primary.main}26`,
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: "50%",
      transform: "translateY(-50%)",
      width: 3,
      height: 24,
      borderRadius: "0 3px 3px 0",
      backgroundColor: theme.palette.primary.main,
    },
    "& $listItemIcon": {
      color: "#FFFFFF",
    },
    "& $listItemText": {
      color: "#FFFFFF",
      fontWeight: 600,
      "& .MuiTypography-root": {
        fontWeight: 600,
      },
    }
  },
  menuRoot: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100%",
    padding: "4px 0",
    color: "#B8C0CC",
    fontFamily: "'Inter', 'Roboto', sans-serif",
    "& .MuiDivider-root": {
      margin: "10px 16px",
      backgroundColor: "rgba(255, 255, 255, 0.08)",
    },
    "& .MuiListSubheader-root": {
      backgroundColor: "transparent",
      color: "#7F8998",
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.08em",
      lineHeight: "28px",
      paddingTop: 12,
      paddingBottom: 4,
      paddingLeft: 20,
      textTransform: "uppercase",
      fontFamily: "'Inter', 'Roboto', sans-serif",
    },
  },

  menuRootCollapsed: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "4px 0",
    minHeight: "100%",
  },
  nestedItem: {
    paddingLeft: 20,
  },
}));

function SectionLabel({ label, collapsed }) {
  if (collapsed) {
    return null;
  }

  return <ListSubheader disableSticky>{label}</ListSubheader>;
}

function ListItemLink(props) {
  const { icon, primary, to, tooltip, showBadge, nested, collapsed } = props;
  const classes = useStyles();
  const { activeMenu } = useActiveMenu();
  const location = useLocation();
  const isActive = activeMenu === to || location.pathname === to;

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  const showTooltip = collapsed || tooltip;

  return (
    <Tooltip title={showTooltip ? primary : ""} placement="right">
      <li>
        <ListItem
          button
          component={renderLink}
          className={[
            classes.listItem,
            nested && !collapsed ? classes.nestedItem : "",
            collapsed ? classes.listItemCollapsed : "",
            isActive ? classes.activeItem : "",
          ].filter(Boolean).join(" ")}
        >
          {icon ? (
            <ListItemIcon
              className={`${classes.listItemIcon} ${collapsed ? classes.listItemIconCollapsed : ""}`}
            >
              {showBadge ? (
                <Badge
                  badgeContent="!"
                  color="error"
                  overlap="circular"
                  className={classes.badge}
                >
                  {icon}
                </Badge>
              ) : (
                icon
              )}
            </ListItemIcon>
          ) : null}
          {!collapsed && (
            <ListItemText
              primary={
                <Typography className={classes.listItemText} noWrap>
                  {primary}
                </Typography>
              }
            />
          )}
        </ListItem>
      </li>
    </Tooltip>
  );
}

const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload;
    const newChats = [];

    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) {
          state[chatIndex] = chat;
        } else {
          newChats.push(chat);
        }
      });
    }

    return [...state, ...newChats];
  }

  if (action.type === "UPDATE_CHATS") {
    const chat = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chat.id);

    if (chatIndex !== -1) {
      state[chatIndex] = chat;
      return [...state];
    } else {
      return [chat, ...state];
    }
  }

  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload;

    const chatIndex = state.findIndex((u) => u.id === chatId);
    if (chatIndex !== -1) {
      state.splice(chatIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "CHANGE_CHAT") {
    const changedChats = state.map((chat) => {
      if (chat.id === action.payload.chat.id) {
        return action.payload.chat;
      }
      return chat;
    });
    return changedChats;
  }
};

const MainListItems = ({ collapsed, drawerClose }) => {
  const theme = useTheme();
  const classes = useStyles();
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user, socket } = useContext(AuthContext);

  const { setActiveMenu } = useActiveMenu();
  const location = useLocation();

  const [connectionWarning, setConnectionWarning] = useState(false);
  const [openCampaignSubmenu, setOpenCampaignSubmenu] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showKanban, setShowKanban] = useState(false);
  const [showOpenAi, setShowOpenAi] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showWavoipCall, setShowWavoipCall] = useState(false);

  // novas features
  const [showSchedules, setShowSchedules] = useState(false);
  const [showInternalChat, setShowInternalChat] = useState(false);
  const [showExternalApi, setShowExternalApi] = useState(false);

  const [invisible, setInvisible] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);
  const [version, setVersion] = useState(false);
  const [campaignHover, setCampaignHover] = useState(false);
  const { list } = useHelps(); // INSERIR
  const [hasHelps, setHasHelps] = useState(false);

  const [openFlowSubmenu, setOpenFlowSubmenu] = useState(false);
  const [flowHover, setFlowHover] = useState(false);

  const { get: getSetting } = useCompanySettings();
  const [showWallets, setShowWallets] = useState(false);

  const isFlowbuilderRouteActive =
    location.pathname.startsWith("/phrase-lists") ||
    location.pathname.startsWith("/flowbuilders");

  useEffect(() => {
    // INSERIR ESSE EFFECT INTEIRO
    async function checkHelps() {
      const helps = await list();
      setHasHelps(helps.length > 0);
    }
    checkHelps();
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const setting = await getSetting(
          {
            "column": "DirectTicketsToWallets"
          }
        );

        setShowWallets(setting.DirectTicketsToWallets);

      } catch (err) {
        toastError(err);
      }
    }

    fetchSettings();
  }, [setShowWallets]);

  const isCampaignRouteActive =
    location.pathname === "/campaigns" ||
    location.pathname.startsWith("/contact-lists") ||
    location.pathname.startsWith("/campaigns-config") ||
    location.pathname.startsWith("/files");

  const renderSubmenuToggle = ({
    label,
    icon,
    open,
    onToggle,
    active,
    onMouseEnter,
    onMouseLeave,
  }) => (
    <Tooltip title={collapsed ? label : ""} placement="right">
      <ListItem
        dense
        button
        className={[
          classes.listItem,
          collapsed ? classes.listItemCollapsed : "",
          active ? classes.activeItem : "",
        ].filter(Boolean).join(" ")}
        onClick={onToggle}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <ListItemIcon
          className={`${classes.listItemIcon} ${collapsed ? classes.listItemIconCollapsed : ""}`}
        >
          {icon}
        </ListItemIcon>
        {!collapsed && (
          <>
            <ListItemText
              primary={
                <Typography className={classes.listItemText} noWrap>
                  {label}
                </Typography>
              }
            />
            {open ? (
              <LuChevronUp className={classes.expandIcon} size={18} strokeWidth={1.75} />
            ) : (
              <LuChevronDown className={classes.expandIcon} size={18} strokeWidth={1.75} />
            )}
          </>
        )}
      </ListItem>
    </Tooltip>
  );

  useEffect(() => {
    if (location.pathname.startsWith("/tickets")) {
      setActiveMenu("/tickets");
    } else {
      setActiveMenu("");
    }
  }, [location, setActiveMenu]);

  const { getPlanCompany } = usePlans();

  const { getVersion } = useVersion();

  useEffect(() => {
    async function fetchVersion() {
      const _version = await getVersion();
      setVersion(_version.version);
    }
    fetchVersion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);

      setShowCampaigns(planConfigs.plan.useCampaigns);
      setShowKanban(planConfigs.plan.useKanban);
      setShowOpenAi(planConfigs.plan.useOpenAi);
      setShowIntegrations(planConfigs.plan.useIntegrations);
      setShowSchedules(planConfigs.plan.useSchedules);
      setShowInternalChat(planConfigs.plan.useInternalChat);
      setShowExternalApi(planConfigs.plan.useExternalApi);
      setShowWavoipCall(planConfigs.plan.wavoip);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchChats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (user.id && socket && typeof socket.on === 'function') {
      const companyId = user.companyId;

      const onCompanyChatMainListItems = (data) => {
        if (data.action === "new-message") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
        if (data.action === "update") {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
      };

      const eventName = `company-${companyId}-chat`;
      console.log('Registrando listener para:', eventName);

      socket.on(eventName, onCompanyChatMainListItems);

      return () => {
        if (socket && typeof socket.off === 'function') {
          console.log('Removendo listener para:', eventName);
          socket.off(eventName, onCompanyChatMainListItems);
        }
      };
    }
  }, [socket, user.id, user.companyId]);

  useEffect(() => {
    let unreadsCount = 0;
    if (chats.length > 0) {
      for (let chat of chats) {
        for (let chatUser of chat.users) {
          if (chatUser.userId === user.id) {
            unreadsCount += chatUser.unreads;
          }
        }
      }
    }
    if (unreadsCount > 0) {
      setInvisible(false);
    } else {
      setInvisible(true);
    }
  }, [chats, user.id]);

  // useEffect(() => {
  //   if (localStorage.getItem("cshow")) {
  //     setShowCampaigns(true);
  //   }
  // }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  const fetchChats = async () => {
    try {
      const { data } = await api.get("/chats/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CHATS", payload: data.records });
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div
      onClick={drawerClose}
      className={collapsed ? classes.menuRootCollapsed : classes.menuRoot}
    >
      <SectionLabel label={SECTION_LABELS.operation} collapsed={collapsed} />

      <Can
        role={
          (user.profile === "user" && user.showDashboard === "enabled") ||
            user.allowRealTime === "enabled"
            ? "admin"
            : user.profile
        }
        perform={"drawer-admin-items:view"}
        yes={() => (
          <Can
            role={
              user.profile === "user" && user.showDashboard === "enabled"
                ? "admin"
                : user.profile
            }
            perform={"drawer-admin-items:view"}
            yes={() => (
              <ListItemLink
                to="/"
                primary={i18n.t("mainDrawer.listItems.dashboard")}
                icon={<SidebarIcon icon={LuLayoutDashboard} />}
                collapsed={collapsed}
                tooltip={collapsed}
              />
            )}
          />
        )}
      />

      <ListItemLink
        to="/tickets"
        primary={i18n.t("mainDrawer.listItems.tickets")}
        icon={<SidebarIcon icon={LuMessagesSquare} />}
        collapsed={collapsed}
        tooltip={collapsed}
      />

      <Can
        role={
          user.profile === "user" && user.allowConnections === "enabled"
            ? "admin"
            : user.profile
        }
        perform={"drawer-admin-items:view"}
        yes={() => (
          <ListItemLink
            to="/connections"
            primary={i18n.t("mainDrawer.listItems.connections")}
            icon={<SidebarIcon icon={LuArrowLeftRight} />}
            showBadge={connectionWarning}
            collapsed={collapsed}
            tooltip={collapsed}
          />
        )}
      />

      {showWavoipCall && (
        <ListItemLink
          to="/call-historicals"
          primary={i18n.t("mainDrawer.listItems.callHistorical")}
          icon={<SidebarIcon icon={LuHistory} />}
                collapsed={collapsed}
                tooltip={collapsed}
        />
      )}

      <SectionLabel label={SECTION_LABELS.clients} collapsed={collapsed} />

      {user.showContacts === "enabled" && (
        <ListItemLink
          to="/contacts"
          primary={i18n.t("mainDrawer.listItems.contacts")}
          icon={<SidebarIcon icon={LuUser} />}
                collapsed={collapsed}
                tooltip={collapsed}
        />
      )}

      <ListItemLink
        to="/tags"
        primary={i18n.t("mainDrawer.listItems.tags")}
        icon={<SidebarIcon icon={LuTag} />}
                collapsed={collapsed}
                tooltip={collapsed}
      />

      <SectionLabel label={SECTION_LABELS.automation} collapsed={collapsed} />

      <ListItemLink
        to="/quick-messages"
        primary={i18n.t("mainDrawer.listItems.quickMessages")}
        icon={<SidebarIcon icon={LuZap} />}
                collapsed={collapsed}
                tooltip={collapsed}
      />

      {showSchedules && (
        <ListItemLink
          to="/schedules"
          primary={i18n.t("mainDrawer.listItems.schedules")}
          icon={<SidebarIcon icon={LuCalendar} />}
                collapsed={collapsed}
                tooltip={collapsed}
        />
      )}

      {user?.showCampaign === "enabled" && showCampaigns && (
        <>
          {renderSubmenuToggle({
            label: i18n.t("mainDrawer.listItems.campaigns"),
            icon: <SidebarIcon icon={LuMegaphone} />,
            open: openCampaignSubmenu,
            onToggle: () => setOpenCampaignSubmenu((prev) => !prev),
            active: isCampaignRouteActive || campaignHover,
            onMouseEnter: () => setCampaignHover(true),
            onMouseLeave: () => setCampaignHover(false),
          })}
          <Collapse
            in={openCampaignSubmenu && !collapsed}
            timeout="auto"
            unmountOnExit
            className={classes.submenuContainer}
          >
            <List dense component="div" disablePadding>
              <ListItemLink
                to="/campaigns"
                primary={i18n.t("campaigns.subMenus.list")}
                icon={<SidebarIcon icon={LuList} />}
                collapsed={collapsed}
                tooltip={collapsed}
                nested
              />
              <ListItemLink
                to="/contact-lists"
                primary={i18n.t("campaigns.subMenus.listContacts")}
                icon={<SidebarIcon icon={LuUsers} />}
                collapsed={collapsed}
                tooltip={collapsed}
                nested
              />
              <ListItemLink
                to="/campaigns-config"
                primary={i18n.t("campaigns.subMenus.settings")}
                icon={<SidebarIcon icon={LuSettings} />}
                collapsed={collapsed}
                tooltip={collapsed}
                nested
              />
              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() => (
                  <ListItemLink
                    to="/files"
                    primary={i18n.t("mainDrawer.listItems.files")}
                    icon={<SidebarIcon icon={LuPaperclip} />}
                collapsed={collapsed}
                tooltip={collapsed}
                    nested
              />
                )}
              />
            </List>
          </Collapse>
        </>
      )}

      {user.showFlow === "enabled" && (
        <>
          {renderSubmenuToggle({
            label: i18n.t("Flowbuilder"),
            icon: <SidebarIcon icon={LuGitBranch} />,
            open: openFlowSubmenu,
            onToggle: () => setOpenFlowSubmenu((prev) => !prev),
            active: isFlowbuilderRouteActive || flowHover,
            onMouseEnter: () => setFlowHover(true),
            onMouseLeave: () => setFlowHover(false),
          })}
          <Collapse
            in={openFlowSubmenu && !collapsed}
            timeout="auto"
            unmountOnExit
            className={classes.submenuContainer}
          >
            <List dense component="div" disablePadding>
              <ListItemLink
                to="/phrase-lists"
                primary={"Fluxo de Campanha"}
                icon={<SidebarIcon icon={LuWorkflow} />}
                collapsed={collapsed}
                tooltip={collapsed}
                nested
              />
              <ListItemLink
                to="/flowbuilders"
                primary={"Fluxo de conversa"}
                icon={<SidebarIcon icon={LuShare2} />}
                collapsed={collapsed}
                tooltip={collapsed}
                nested
              />
            </List>
          </Collapse>
        </>
      )}

      {showKanban && (
        <ListItemLink
          to="/kanban"
          primary={i18n.t("mainDrawer.listItems.kanban")}
          icon={<SidebarIcon icon={LuColumns} />}
                collapsed={collapsed}
                tooltip={collapsed}
        />
      )}

      {showInternalChat && (
        <ListItemLink
          to="/chats"
          primary={i18n.t("mainDrawer.listItems.chats")}
          icon={
            <Badge color="secondary" variant="dot" invisible={invisible}>
              <SidebarIcon icon={LuMessageCircle} />
            </Badge>
          }          collapsed={collapsed}
          tooltip={collapsed}
        />
      )}

      {hasHelps && (
        <ListItemLink
          to="/helps"
          primary={i18n.t("mainDrawer.listItems.helps")}
          icon={<SidebarIcon icon={LuHelpCircle} />}
                collapsed={collapsed}
                tooltip={collapsed}
        />
      )}

      <SectionLabel label={SECTION_LABELS.insights} collapsed={collapsed} />

      <Can
        role={
          (user.profile === "user" && user.showDashboard === "enabled") ||
            user.allowRealTime === "enabled"
            ? "admin"
            : user.profile
        }
        perform={"drawer-admin-items:view"}
        yes={() => (
          <>
            <Can
              role={
                user.profile === "user" && user.showDashboard === "enabled"
                  ? "admin"
                  : user.profile
              }
              perform={"drawer-admin-items:view"}
              yes={() => (
                <ListItemLink
                  to="/reports"
                  primary={i18n.t("mainDrawer.listItems.reports")}
                  icon={<SidebarIcon icon={LuFileBarChart} />}
                collapsed={collapsed}
                tooltip={collapsed}
                />
              )}
            />
            <Can
              role={
                user.profile === "user" && user.allowRealTime === "enabled"
                  ? "admin"
                  : user.profile
              }
              perform={"drawer-admin-items:view"}
              yes={() => (
                <ListItemLink
                  to="/moments"
                  primary={i18n.t("mainDrawer.listItems.chatsTempoReal")}
                  icon={<SidebarIcon icon={LuActivity} />}
                collapsed={collapsed}
                tooltip={collapsed}
                />
              )}
            />
            {user.profile === "admin" && showWallets && (
              <ListItemLink
                to="/wallets"
                primary={i18n.t("mainDrawer.listItems.wallets")}
                icon={<SidebarIcon icon={LuWallet} />}
                collapsed={collapsed}
                tooltip={collapsed}
              />
            )}
          </>
        )}
      />

      <SectionLabel
        label={i18n.t("mainDrawer.listItems.administration")}
        collapsed={collapsed}
      />

      <Can
        role={
          user.profile === "user" && user.allowConnections === "enabled"
            ? "admin"
            : user.profile
        }
        perform="dashboard:view"
        yes={() => (
          <>
            {user.super && (
              <ListItemLink
                to="/announcements"
                primary={i18n.t("mainDrawer.listItems.annoucements")}
                icon={<SidebarIcon icon={LuBellRing} />}
                collapsed={collapsed}
                tooltip={collapsed}
              />
            )}

            {showExternalApi && (
              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() => (
                  <ListItemLink
                    to="/messages-api"
                    primary={i18n.t("mainDrawer.listItems.messagesAPI")}
                    icon={<SidebarIcon icon={LuCode} />}
                collapsed={collapsed}
                tooltip={collapsed}
                  />
                )}
              />
            )}

            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() => (
                <ListItemLink
                  to="/users"
                  primary={i18n.t("mainDrawer.listItems.users")}
                  icon={<SidebarIcon icon={LuUsers} />}
                collapsed={collapsed}
                tooltip={collapsed}
                />
              )}
            />

            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() => (
                <ListItemLink
                  to="/birthday-settings"
                  primary={i18n.t("mainDrawer.listItems.birthdaySettings")}
                  icon={<SidebarIcon icon={LuCake} />}
                collapsed={collapsed}
                tooltip={collapsed}
                />
              )}
            />

            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() => (
                <ListItemLink
                  to="/queues"
                  primary={i18n.t("mainDrawer.listItems.queues")}
                  icon={<SidebarIcon icon={LuNetwork} />}
                collapsed={collapsed}
                tooltip={collapsed}
                />
              )}
            />

            {showOpenAi && (
              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() => (
                  <ListItemLink
                    to="/prompts"
                    primary={i18n.t("mainDrawer.listItems.prompts")}
                    icon={<SidebarIcon icon={LuSparkles} />}
                collapsed={collapsed}
                tooltip={collapsed}
                  />
                )}
              />
            )}

            {showIntegrations && (
              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() => (
                  <ListItemLink
                    to="/queue-integration"
                    primary={i18n.t("mainDrawer.listItems.queueIntegration")}
                    icon={<SidebarIcon icon={LuPlug} />}
                collapsed={collapsed}
                tooltip={collapsed}
                  />
                )}
              />
            )}

            {user.super && (
              <ListItemLink
                to="/allConnections"
                primary={i18n.t("mainDrawer.listItems.allConnections")}
                icon={<SidebarIcon icon={LuSmartphone} />}
                collapsed={collapsed}
                tooltip={collapsed}
              />
            )}

            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() => (
                <ListItemLink
                  to="/financeiro"
                  primary={i18n.t("mainDrawer.listItems.financeiro")}
                  icon={<SidebarIcon icon={LuBanknote} />}
                collapsed={collapsed}
                tooltip={collapsed}
                />
              )}
            />

            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() => (
                <ListItemLink
                  to="/settings"
                  primary={i18n.t("mainDrawer.listItems.settings")}
                  icon={<SidebarIcon icon={LuSettings} />}
                collapsed={collapsed}
                tooltip={collapsed}
                />
              )}
            />

            {user.super && (
              <ListItemLink
                to="/companies"
                primary={i18n.t("mainDrawer.listItems.companies")}
                icon={<SidebarIcon icon={LuBuilding2} />}
                collapsed={collapsed}
                tooltip={collapsed}
              />
            )}
          </>
        )}
      />

      {!collapsed && (
        <Typography className={classes.versionContainer}>
          {`${version}`}
        </Typography>
      )}
    </div>
  );
};

export default MainListItems;